import getErrorMessage from "api/get-error-message";
import useApiUserAuthEmailVerificationStart from "api/user/auth/use-api-user-auth-email-verification-start";
import useLogout from "common/auth/hooks/use-logout";
import { EMAIL_VALIDATION } from "common/validation";
import { Alert } from "components/alert";
import { Button } from "components/button";
import { Card } from "components/card";
import { InputField } from "components/input-field";
import PageWrapper from "components/page-wrapper";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useIntercom } from "react-use-intercom";
import { useAppSelector } from "store";
import { authNewSelectors } from "store/reducers/auth-new";

import css from "./index.module.scss";

type FormData = {
  email: string;
};

const AuthEmailVerificationPage = () => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<boolean>(false);
  const logout = useLogout();
  const { mutateAsync, status } = useApiUserAuthEmailVerificationStart();
  const { showNewMessage } = useIntercom();
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormData>({
    defaultValues: {
      email: useAppSelector(authNewSelectors.userEmailGet),
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    setError("");
    setSuccess(false);

    try {
      await mutateAsync({
        email: formData.email,
        redirectPath: window.location.pathname + window.location.search,
      });
      setSuccess(true);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  });

  return (
    <PageWrapper centerVertically maxWidth={500} title="Verify your email">
      <Card>
        <h3 className={css.title}>Verify your email</h3>
        <p className={css.text}>
          Please confirm your email address. If you need to edit the email
          address you originally provided you can do so below.
        </p>
        {error && <Alert type="error">{error}</Alert>}
        {success && (
          <Alert className={css.alert} type="success">
            Please check your inbox. You should find a link to verify your
            email.
          </Alert>
        )}
        <form onSubmit={onSubmit}>
          <InputField
            {...register("email", {
              pattern: EMAIL_VALIDATION,
              required: true,
            })}
            className={css.input}
            placeholder="Your email"
            size="large"
            state={errors.email ? "error" : undefined}
            type="email"
          />
          <Button fullWidth loading={status === "pending"} size="large">
            Send verification email
          </Button>
        </form>
        <div className={css.note}>
          <a onClick={() => showNewMessage()}>Contact support</a> or{" "}
          <a onClick={() => logout()}>Logout</a>.
        </div>
      </Card>
    </PageWrapper>
  );
};

export default AuthEmailVerificationPage;
